import {applySnapshot, types} from 'mobx-state-tree';
import moment from 'moment';
import {rootStore} from '.';
import i18n from 'i18next';
import { getLocalizedField } from 'helpers/localizedField';
import CompanyService from 'services/CompanyService';
import WorkSchduleService from 'services/WorkSchduleService';

export const AnnualLeaveAccrual = types
    .model('AnnualLeaveAccrual', {
        // Filter parameters
        filterParams: types.optional(
            types.model('AccrualFilterParams', {
                year: types.optional(types.string, moment().format('YYYY')),
                name: types.optional(types.string, ''),
                hireType: types.optional(types.string, ''),
                area: types.optional(types.maybe(types.number), 0),
            }),
            {},
        ),

        // Data lists for filters
        year: types.optional(types.array(types.number), []),
        hireTypeList: types.optional(types.array(types.frozen()), []),
        workPlaceList: types.optional(types.array(types.frozen()), []),

        // Table data
        accrualData: types.optional(types.array(types.frozen()), []),
        dataForDownloading: types.optional(types.array(types.frozen()), []),
        deletedRows: types.optional(types.array(types.frozen()), []),
        dataCount: types.optional(types.number, 0),

        // New rows for inline editing
        newRows: types.optional(
            types.array(
                types.model('NewAccrualRow', {
                    rowId: types.optional(types.string, ''),
                    userId: types.optional(types.maybe(types.number), 0),
                    companyId: types.optional(types.maybe(types.number), 0),
                    effectStartDate: types.optional(types.string, ''),
                    effectEndDate: types.optional(types.string, ''),
                    occurDays: types.optional(types.maybe(types.number), 0),
                    isAdditionalLeave: types.optional(types.boolean, false),
                    isAccrualTransfer: types.optional(types.boolean, true),
                    status: types.optional(types.string, 'ADD'),
                }),
            ),
            [],
        ),

        // Selected employee (from SelectEmployModal)
        selectedEmployee: types.maybeNull(types.frozen(), {}),
        employeeList: types.optional(types.array(types.frozen()), []),
        employeeListLoading: types.optional(types.boolean, false),
        currentEditingRowId: types.optional(types.string, ''),

        // Common
        page: types.optional(types.number, 1),
        size: types.optional(types.number, 500),
        isLoading: types.optional(types.boolean, false),
    })
    .views((self) => ({
        get get() {
            try {
                return { ...self };
            } catch (err) {
                console.error(err);
            }
            return {};
        },
    }))
    .actions((self) => ({
        generateRowId(item = {}, index = 0) {
            if (item?.rowId) {
                return item.rowId;
            }

            const stableId = item?.id ?? item?.userId ?? item?.uuid ?? item?.companyId;
            if (stableId !== undefined && stableId !== null && stableId !== '') {
                return `row-${stableId}-${index}`;
            }

            return `row-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`;
        },

        addRowIdToItems(data = []) {
            return data.map((item, index) => ({
                ...item,
                isAccrualTransfer: item?.isAccrualTransfer !== false,
                rowId: self.generateRowId(item, index),
            }));
        },

        reset() {
            applySnapshot(self, {});
        },

        // Filter actions
        setFilterParams(params) {
            const currentFilters = self.filterParams;
            Object.keys(params).forEach((key) => {
                if (key in currentFilters) {
                    if (key === 'area') {
                        currentFilters[key] = Number(params[key]);
                    } else {
                        currentFilters[key] = params[key];
                    }
                }
            });
        },

        resetFilters() {
            const f = self.filterParams;
            f.year = moment().format('YYYY');
            f.name = '';
            f.hireType = '';
            f.area = 0;
        },

        resetData() {
            self.page = 1;
        },

        // Data setters
        setYear(yearList) {
            self.year = yearList;
        },

        setHireTypeList(list) {
            self.hireTypeList = list;
        },

        setWorkPlaceList(list) {
            self.workPlaceList = list;
        },

        setAccrualData(data) {
            const dataWithRowId = self.addRowIdToItems(data || []);
            self.accrualData.replace(dataWithRowId);
        },

        setDataForDownloading(data) {
            self.dataForDownloading.replace(data || []);
        },

        setSelectedEmployee(employee) {
            self.selectedEmployee = employee;
        },

        clearSelectedEmployee() {
            self.selectedEmployee = {};
        },

        setEmployeeList(list) {
            self.employeeList.replace(list || []);
        },

        setEmployeeListLoading(loading) {
            self.employeeListLoading = loading;
        },

        setCurrentEditingRowId(rowId) {
            self.currentEditingRowId = rowId;
        },

        resetEmployeeModal() {
            self.employeeList.clear();
        },

        normalizeEmployeeData(data) {
            return (data || []).map((item) => {
                const user = item?.user || item || {};
                return {
                    ...user,
                    userId: user?.id ?? item?.userId ?? item?.id,
                    companyNumber: item?.companyNumber ?? '',
                    name: user?.name ?? item?.name ?? '',
                    profile: user?.profile ?? item?.profile ?? null,
                    departmentName: item?.department?.department?.name || item?.departmentName || '',
                    rankName: item?.rank?.name || item?.rankName || '',
                    rank: item?.rank || null, // 직위 표시 번역용(nameEng·nameVie·nameChn)
                    raw: item,
                };
            });
        },

        // New row actions
        addNewRow() {
            const newRow = {
                rowId: self.generateRowId(),
                userId: 0,
                companyNumber: '',
                name: '',
                nameEngFirst: '',
                nameEngLast: '',
                nameVie: '',
                nameChn: '',
                companyId: 0,
                effectStartDate: '',
                effectEndDate: '',
                occurDays: null,
                isAdditionalLeave: false,
                isAccrualTransfer: true,
                status: 'ADD',
            };
            self.accrualData.replace([...self.accrualData, newRow]);
        },

        deleteNewRow(rowId) {
            const index = self.accrualData.findIndex((row) => row.rowId === rowId);
            if (index > -1) {
                self.accrualData.splice(index, 1);
            }
        },

        deleteRowsByRowIds(rowIds = []) {
            const deleteSet = new Set(rowIds || []);
            if (deleteSet.size === 0) {
                return;
            }

            const rowsToDelete = self.accrualData
                .filter((row) => deleteSet.has(row.rowId))
                .map((row) => ({
                    ...row,
                    status: 'DEL',
                }));

            const mergedDeletedRows = self.deletedRows.slice();
            rowsToDelete.forEach((row) => {
                const existIndex = mergedDeletedRows.findIndex((item) => item.rowId === row.rowId);
                if (existIndex > -1) {
                    mergedDeletedRows[existIndex] = row;
                } else {
                    mergedDeletedRows.push(row);
                }
            });

            const remainRows = self.accrualData.filter((row) => !deleteSet.has(row.rowId));

            self.deletedRows.replace(mergedDeletedRows);
            self.accrualData.replace(remainRows);
        },

        updateNewRow(rowId, updates) {
            const rowIndex = self.accrualData.findIndex((row) => row.rowId === rowId);
            if (rowIndex < 0) {
                return;
            }

            const currentRow = self.accrualData[rowIndex] || {};
            const safeUpdates = Object.keys(updates || {}).reduce((acc, key) => {
                if (key in currentRow) {
                    acc[key] = updates[key];
                }
                return acc;
            }, {});

            const nextRow = {
                ...currentRow,
                ...safeUpdates,
                status: 'ADD',
            };

            const nextData = self.accrualData.slice();
            nextData[rowIndex] = nextRow;
            self.accrualData.replace(nextData);
        },

        clearNewRows() {
            self.newRows.clear();
        },

        clearDeletedRows() {
            self.deletedRows.clear();
        },

        setDataCount(count) {
            self.dataCount = count;
        },

        setPage(page) {
            self.page = page;
        },

        setIsLoading(loading) {
            self.isLoading = loading;
        },

        setIsBackgroundLoading(loading) {
            self.isBackgroundLoading = loading;
        },

        // API calls
        async getCommCodes() {
            const { company } = rootStore;
            try {
                // 고용유형 데이터
                const response = await CompanyService.getCommCodeNew({
                    companyId: company.id,
                    commCodeType: 'HIRE_TYPE'
                });

                const hireTypeList =
                    response?.data?.map(v => {
                        return { value: v?.detail_seq, label: getLocalizedField(v, 'comm_code_name') };
                    }) || [];
                self.setHireTypeList(hireTypeList);
                return hireTypeList;
            } catch (error) {
                console.error('Error fetching employee types:', error);
                return [];
            }
        },

        async getWorkPlace() {
            const { company } = rootStore;
            try {
                const response = await CompanyService.getWorkingArea(company.id);
                if (response.data) {
                    const workPlaceList =
                        response?.data?.map((v) => ({
                            value: v?.id,
                            label: v?.name === '' ? i18n.t('workFlexible.headquarters') : getLocalizedField(v),
                        })) || [];
                    self.setWorkPlaceList(workPlaceList);
                    return workPlaceList;
                }
                return [];
            } catch (error) {
                console.error('Error fetching work places:', error);
                return [];
            }
        },

        async getYear() {
            try {
                const response = await CompanyService.getYearList();

                self.setYear(response?.data);
                return response?.data || [];
            } catch (error) {
                console.error('Error fetching year list:', error);
                return [];
            }
        },

        async fetchEmployeeList(searchText = '') {
            const { company } = rootStore;
            self.setEmployeeListLoading(true);
            try {
                const DeadlineService = require('services/DeadlineService').default;
                const response = await DeadlineService.getUserList(
                    company.get.id,
                    searchText,
                    'SUCCESS',
                    'list',
                );
                const data = response?.data?.data || [];
                self.setEmployeeList(self.normalizeEmployeeData(data));
            } catch (error) {
                console.error('Error loading employee list:', error);
                self.setEmployeeList([]);
            } finally {
                self.setEmployeeListLoading(false);
            }
        },

        /**
         * Load accrual list data with background pagination
         */
        async getAccrualList() {
            self.setIsLoading(true);

            const companyId = rootStore.company.get.id;
            const currentFilters = self.filterParams;
            const area = Number(currentFilters?.area) || null;
            const { area: _, ...rest } = currentFilters;

            try {
                const firstResponse = await WorkSchduleService.getLeaveAccrualList({
                    companyId,
                    area,
                    ...rest,
                    hireType: currentFilters?.hireType || null,
                });

                const totalCount = firstResponse?.data?.length;
                const dataWithRowId = self.addRowIdToItems(firstResponse?.data || []);
                self.setAccrualData(dataWithRowId);
                self.setDataCount(totalCount);

                return firstResponse?.data || [];
            } catch (error) {
                console.error('Error loading accrual list:', error);
                return [];
            } finally {
                self.setIsLoading(false);
            }
        },

        async getDataDownload() {
            self.setIsLoading(true);

            const companyId = rootStore.company.get.id;
            const currentFilters = self.filterParams;

            const area = Number(currentFilters?.area) || null;
            const { area: _, ...rest } = currentFilters;

            try {
                const response = await WorkSchduleService.getLeaveAccrualList({
                    companyId,
                    area,
                    ...rest,
                    hireType: currentFilters?.hireType || null,
                });

                const formattedData = (response?.data || []).map(item => ({
                    ...item,
                    effectStartDate: moment(item.effectStartDate).format('YYYY-MM-DD'),
                    effectEndDate: moment(item.effectEndDate).format('YYYY-MM-DD'),
                }));

                self.setDataForDownloading(formattedData);
                return formattedData;
            } catch (error) {
                console.error('Error loading download data:', error);
                self.setDataForDownloading([]);
                return [];
            } finally {
                self.setIsLoading(false);
            }
        },

        async saveNewRows() {
            self.setIsLoading(true);
            try {
                const payload = [...self.accrualData, ...self.deletedRows]
                    .map(row => ({
                        ...row,
                        effectEndDate: row.effectEndDate || '9999-12-31'
                    }))
                    .filter(row => row.status);

                const res = await WorkSchduleService.merLeaveAccrual(payload);
                self.getAccrualList();
                self.clearDeletedRows();

                return res.data;
            } catch (error) {
                return { success: false, error };
            } finally {
                self.setIsLoading(false);
            }
        },

        async saveUploadedRows(rows = []) {
            self.setIsLoading(true);
            try {
                const companyId = rootStore.company.get.id;

                const payload = rows.map((row) => ({
                    userId: self.employeeList.find(
                        (employee) =>
                          String(employee?.companyNumber).trim() === String(row?.companyNumber).trim(),
                    )?.userId,
                    companyId,
                    effectStartDate: String(row?.effectStartDate || '').trim(),
                    effectEndDate: String(row?.effectEndDate || '').trim(),
                    occurDays:
                        row?.occurDays === null || row?.occurDays === undefined || row?.occurDays === ''
                            ? null
                            : Number(row?.occurDays),
                    isAdditionalLeave: row?.isAdditionalLeave === 'Y',
                    isAccrualTransfer: row?.isAccrualTransfer !== 'N',
                    status: 'ADD',
                }));

                const res = await WorkSchduleService.merLeaveAccrual(payload);
                await self.getAccrualList();
                return res;
            } catch (error) {
                console.error('Error saving uploaded rows:', error);
                return { success: false, error };
            } finally {
                self.setIsLoading(false);
            }
        },

        /**
         * Initialize the model: load filter data and first page
         */
        async initialize() {
            await Promise.all([self.getCommCodes(), self.getWorkPlace(), self.getYear()]);
            await self.getAccrualList();
        },
    }));
